import { useEffect, useId, useState, type ReactNode } from 'react';
import { ApiError } from '../api';

export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function Loading({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="loading" role="status" aria-live="polite">
      <span className="spinner" aria-hidden /> {label}
    </div>
  );
}

export function ErrorAlert({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null;
  const message = error instanceof ApiError || error instanceof Error ? error.message : 'Erro inesperado.';
  const issues = error instanceof ApiError ? error.fieldIssues : [];
  return (
    <div className="alert alert-error" role="alert">
      <strong>{title ?? 'Não foi possível concluir.'}</strong>
      {message}
      {issues.length > 0 && (
        <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
          {issues.map((i) => (
            <li key={i.field + i.message}>{i.message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function fieldError(error: unknown, field: string): string | undefined {
  return error instanceof ApiError ? error.fieldIssues.find((i) => i.field === field)?.message : undefined;
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: (props: { id: string; 'aria-invalid'?: true; 'aria-describedby'?: string; required?: boolean }) => ReactNode;
}

// Campo com rótulo associado, dica e erro acessíveis (seção 18).
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const describedBy = [hint && `${id}-hint`, error && `${id}-err`].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required && <span aria-hidden> *</span>}
      </label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy, required })}
      {hint && (
        <span className="hint" id={`${id}-hint`}>
          {hint}
        </span>
      )}
      {error && (
        <span className="err" id={`${id}-err`}>
          {error}
        </span>
      )}
    </div>
  );
}

// Bloco de funcionalidade ainda não entregue: mostra a etapa prevista, nunca dados fictícios.
export function Pending({ title, stage, children }: { title: string; stage: string; children?: ReactNode }) {
  return (
    <div className="pending-block">
      <strong>{title}</strong> <span className="badge">etapa {stage}</span>
      <div className="small" style={{ marginTop: '0.25rem' }}>
        {children ?? 'Esta funcionalidade será entregue na etapa indicada do piloto.'}
      </div>
    </div>
  );
}

export function Dialog({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  const id = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={id}>
        <h2 id={id}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function DemoBadge({ show }: { show: boolean }) {
  return show ? <span className="badge badge-demo">DEMONSTRAÇÃO</span> : null;
}

export function ActiveBadge({ active }: { active: boolean }) {
  return active ? <span className="badge badge-ok">Ativo</span> : <span className="badge badge-off">Inativo</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="muted" style={{ padding: '1rem 0' }}>{children}</p>;
}

// Quadro "Como funciona": explicação curta e prática no topo das telas. Aberto/fechado fica lembrado neste aparelho.
export function HowTo({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  const key = `howto:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem(key) !== 'fechado';
    } catch {
      return true;
    }
  });
  return (
    <details
      className="card howto"
      open={open}
      onToggle={(ev) => {
        const isOpen = (ev.currentTarget as HTMLDetailsElement).open;
        setOpen(isOpen);
        try {
          window.localStorage.setItem(key, isOpen ? 'aberto' : 'fechado');
        } catch {
          /* sem armazenamento: só não lembra */
        }
      }}
    >
      <summary>
        <strong>{title}</strong>
      </summary>
      <div style={{ marginTop: '0.5rem' }}>{children}</div>
    </details>
  );
}
